import { combineReducers } from 'redux';
import { takeLatest } from 'redux-saga/effects';

import {
    ProgramDetailsPayload,
    ProgramListPayload,
    details as detailsApiCall,
    list as listApiCall,
    create as createApiCall,
    update as updateApiCall,
    remove as removeApiCall,
} from '../api/programs';
import { RequestState, MainReducerState } from '../reducers';
import { ListResponse, Program } from '../api/apiTypes';
import { EzeeAsyncAction } from '../helpers/EzeeAsyncAction';
import { simpleAsyncSaga } from '../helpers/EzeeSaga';

// State

export interface ProgramsState {
    list: RequestState<ListResponse<Program>>;
    search: RequestState<ListResponse<Program>>;
    details: RequestState<Program | undefined>;
    create: RequestState<Program | undefined>;
    update: RequestState<Program | undefined>;
    remove: RequestState<Program | undefined>;
}

const initialState: ProgramsState = {
    list: {
        data: {
            items: [],
            totalCount: 0,
            page: 0,
            pageSize: 20,
            pageCount: 0,
        },
        loading: false,
        success: false,
    },
    search: {
        data: {
            items: [],
            totalCount: 0,
            page: 0,
            pageSize: 20,
            pageCount: 0,
        },
        loading: false,
        success: false,
    },
    details: {
        data: undefined,
        loading: false,
        success: false,
    },
    create: {
        data: undefined,
        loading: false,
        success: false,
    },
    update: {
        data: undefined,
        loading: false,
        success: false,
    },
    remove: {
        data: undefined,
        loading: false,
        success: false,
    },
};

// Actions/Reducers

export const list = new EzeeAsyncAction<
    ProgramsState['list'],
    ProgramListPayload,
    ListResponse<Program>
>('programs/list', initialState.list, {
    trigger: (state, payload) => ({
        ...state,
        loading: true,
    }),
    success: (state, payload) => ({
        data: payload,
        loading: false,
    }),
    failure: (state, payload) => ({
        ...state,
        loading: false,
        error: payload,
    }),
    reset: (state) => ({
        ...initialState.list,
    }),
});

export const search = new EzeeAsyncAction<
    ProgramsState['search'],
    ProgramListPayload,
    ListResponse<Program>
>('programs/search', initialState.search, {
    trigger: (state, payload) => ({
        ...state,
        loading: true,
    }),
    success: (state, payload) => ({
        data: payload,
        loading: false,
    }),
    failure: (state, payload) => ({
        ...state,
        loading: false,
        error: payload,
    }),
    reset: (state) => ({
        ...initialState.search,
    }),
});

export const details = new EzeeAsyncAction<
    ProgramsState['details'],
    ProgramDetailsPayload,
    Program
>('programs/details', initialState.details, {
    trigger: (state, payload) => ({
        ...state,
        loading: true,
        success: undefined,
        error: undefined,
    }),
    success: (state, payload) => ({
        data: payload,
        loading: false,
        success: true,
    }),
    failure: (state, payload) => ({
        ...state,
        loading: false,
        error: payload,
    }),
    reset: (state) => ({
        ...initialState.details,
    }),
    addWeek: (state, payload) => {
        payload.tasks = [];
        state.data?.weeks?.push(payload);
        return {...state};
    },
    removeWeek: (state, payload) => {
        if (state.data?.weeks) {
            state.data.weeks = [...state.data.weeks.filter((week) => week.id !== payload.id)];
        }
        return {...state};
    },
    removeTask: (state, payload) => {
        if (state.data?.weeks) {
            state.data.weeks.forEach((week) => {
                if (week.tasks) {
                    week.tasks = [...week.tasks.filter((task) => task.id !== payload.id)];
                    week.points = week.tasks.map((task) => task.points).reduce((a, b) => a + b, 0);
                }
            });
        }
        return {...state};
    },
});

export const create = new EzeeAsyncAction<
    ProgramsState['create'],
    Program,
    Program
>('programs/create', initialState.create, {
    trigger: (state, payload) => ({
        ...state,
        loading: true,
        success: undefined,
        payload,
    }),
    success: (state, payload) => ({
        ...state,
        data: payload,
        loading: false,
        success: true,
    }),
    failure: (state, payload) => ({
        ...state,
        loading: false,
        error: payload,
        success: false,
    }),
    reset: (state) => ({
        ...initialState.create,
    }),
});

export const update = new EzeeAsyncAction<
    ProgramsState['update'],
    Program,
    Program
>('programs/update', initialState.update, {
    trigger: (state, payload) => ({
        ...state,
        loading: true,
        success: undefined,
        payload,
    }),
    success: (state, payload) => ({
        ...state,
        data: payload,
        loading: false,
        success: true,
    }),
    failure: (state, payload) => ({
        ...state,
        loading: false,
        error: payload,
        success: false,
    }),
    reset: (state) => ({
        ...initialState.update,
    }),
});

export const remove = new EzeeAsyncAction<
    ProgramsState['remove'],
    Program,
    Program
>('programs/remove', initialState.remove, {
    trigger: (state, payload) => ({
        ...state,
        loading: true,
        success: undefined,
    }),
    success: (state, payload) => ({
        data: payload,
        loading: false,
        success: true,
    }),
    failure: (state, payload) => ({
        ...state,
        loading: false,
        error: payload,
        success: false,
    }),
    reset: (state) => ({
        ...initialState.remove,
    }),
});

// Reducer

export const programsReducer = combineReducers<ProgramsState>({
    details: details.reducer,
    list: list.reducer,
    search: search.reducer,
    create: create.reducer,
    update: update.reducer,
    remove: remove.reducer,
});

// Saga

export function* programsSaga() {
    yield takeLatest(list.type.trigger, simpleAsyncSaga(listApiCall, list));
    yield takeLatest(search.type.trigger, simpleAsyncSaga(listApiCall, search));
    yield takeLatest(details.type.trigger, simpleAsyncSaga(detailsApiCall, details));
    yield takeLatest(create.type.trigger, simpleAsyncSaga(createApiCall, create));
    yield takeLatest(update.type.trigger, simpleAsyncSaga(updateApiCall, update));
    yield takeLatest(remove.type.trigger, simpleAsyncSaga(removeApiCall, remove));
}

// Store helpers

export const getProgramsState = (state: MainReducerState) => state.programs;
